import {
  DemandeHabilitationDocument,
  DemandeHabilitation as DemandeHabilitationGql,
  DemandeHabilitationTypeEnum,
  DemandeValidationPj as DemandeValidationPjGql,
  HabilitationDocumentCode,
} from '@graphql/generated';
import { DocumentMapper } from './document-mapper';
import {
  DemandeHabilitation,
  HabilitationDocument,
} from '@lib/models/demande-habilitation/demande-habilitation.model';
import { DemandeHabilitationStatutMapper } from './demande-habilitation-statut-mapper';
import { DemandeValidationPjMapper } from './demande-validation-pj-mapper';
import { HabilitationType } from '@lib/models/demande-habilitation/types/habilitation-type.type';
import { HabilitationParcoursMapper } from './habilitation-parcours-mapper';
import { StatutCollaborationStefi } from '@lib/models/demande-habilitation/habilitation-parcours.model';
import { DocumentType } from '@lib/ged/document-types.type';
import { DocumentTypeMapper } from './document-type-mapper';
import { getHabilitationDocumentCandidateTypes } from '@lib/models/demande-habilitation/habilitation-parcours-document.mapper';
import {
  getHabilitationStepPresentation,
  HABILITATION_DOCUMENT_PRESENTATION,
} from '@lib/models/demande-habilitation/habilitation-parcours-presentation';

export class DemandeHabilitationMapper {
  public static fromGraphQL(
    input: DemandeHabilitationGql,
    isBackOffice: boolean
  ): DemandeHabilitation {
    const typeHab = this.getHabilitationType(input);
    const documentTypes = input.parcours
      ? input.parcours.documents.flatMap(document =>
          getHabilitationDocumentCandidateTypes(
            HABILITATION_DOCUMENT_PRESENTATION[document.code as HabilitationDocumentCode]
              .code
          )
        )
      : [];
    const habDocuments = input?.demandeHabilitationDocuments
      ? this.getHabilitationsDocument(
          input.demandeHabilitationDocuments,
          isBackOffice
        ).filter(hab => {
          const documentType = this.getDocumentType(hab);
          return (
            !this.isRefused(hab) &&
            (!input.parcours ||
              (documentType
                ? documentTypes.includes(documentType) ||
                  this.isGeneratedParcoursDocument(typeHab, documentType)
                : false))
          );
        })
      : undefined;
    return {
      id: input.id,
      publicId: input.publicId,
      nomUsage: input?.nomUsage ?? undefined,
      prenom: input?.prenom ?? undefined,
      email: input?.email ?? undefined,
      type: typeHab,
      codeCom: input.codeCom!,
      isDraft: input.statut === 'EN_COURS',
      etapeCourante: input.etapeCourante
        ? getHabilitationStepPresentation(typeHab, input.etapeCourante).code
        : undefined,
      dateUpdate: input.dateUpdate ? new Date(input.dateUpdate) : undefined,
      dateDeclaration: input.dateDeclaration,
      statut: DemandeHabilitationStatutMapper.fromGraphQl(input.statut),
      gestionnaire: input.gestionnaire ?? undefined,
      gestionnaireId: input.gestionnaireId ?? undefined,
      habilitationDocuments: habDocuments,
      parcoursHabilitation: input.parcours
        ? HabilitationParcoursMapper.fromGraphQl(
            input.parcours,
            typeHab,
            isBackOffice ? 'shm' : 'consultant',
            this.getStefiCollaboration(input)
          )
        : undefined,
      financement: input.financement
        ? {
            collaborationStefiStatut: input.financement.collaborationStefiStatut,
            capaciteProfessionnelleStatut:
              input.financement.capaciteProfessionnelleStatut,
            demandeInscriptionOrias: input.financement.demandeInscriptionOrias,
            finalisationInscriptionOrias: input.financement.finalisationInscriptionOrias,
            declarationMandatOrias: input.financement.declarationMandatOrias,
            confirmationDeclarationOrias: input.financement.confirmationDeclarationOrias,
            carteDemarchageGeneree: input.financement.carteDemarchageGeneree ?? undefined,
            oriasStatut: input.financement.oriasStatut,
          }
        : undefined,
      immobilier: input.immobilier
        ? {
            attestationCollaborateurApercuGedId: isBackOffice
              ? (input.immobilier.attestationCollaborateurApercuGedId ?? undefined)
              : undefined,
            attestationCollaborateurApercuFileName: isBackOffice
              ? (input.immobilier.attestationCollaborateurApercuFileName ?? undefined)
              : undefined,
            dateDelivranceAttestationCollaborateur: isBackOffice
              ? input.immobilier.dateDelivranceAttestationCollaborateur
                ? new Date(input.immobilier.dateDelivranceAttestationCollaborateur)
                : undefined
              : undefined,
            numeroAdcAttestationCollaborateur: isBackOffice
              ? (input.immobilier.numeroAdcAttestationCollaborateur ?? undefined)
              : undefined,
          }
        : undefined,
      refus: input.refus
        ? {
            id: input.refus.id,
            dateRefus: input.refus.dateRefus,
            gestionnare: input.refus.gestionnaireId,
            commentaire: input.refus.commentaire,
          }
        : undefined,
    };
  }

  private static getHabilitationType(input: DemandeHabilitationGql): HabilitationType {
    switch (input.type) {
      case DemandeHabilitationTypeEnum.Assurance:
        return 'assurance';
      case DemandeHabilitationTypeEnum.Financement:
        return 'financement';
      case DemandeHabilitationTypeEnum.Immobilier:
        return 'immobilier';
      case DemandeHabilitationTypeEnum.Cif:
        return 'cif';
      case DemandeHabilitationTypeEnum.Alpsi:
        return 'alpsi';
      default:
        throw new Error(`Unsupported habilitation type: ${String(input.type)}`);
    }
  }

  private static getStefiCollaboration(
    input: DemandeHabilitationGql
  ): StatutCollaborationStefi | undefined {
    switch (input.financement?.collaborationStefiStatut) {
      case 'COLLABORATION_CHOISIE':
        return 'collaboration_choisie';
      case 'CONTRAT_EN_ATTENTE':
        return 'contrat_en_attente';
      case 'CONTRAT_EN_SIGNATURE':
        return 'contrat_en_signature';
      case 'SIGNEE':
        return 'signee';
      case 'SANS_COLLABORATION':
        return 'sans_collaboration';
      case 'INDECISE':
      case 'BLOQUEE_INTEGRATION':
        return 'indecise';
      default:
        return undefined;
    }
  }

  private static getDocumentType(
    habilitationDocument: HabilitationDocument
  ): DocumentType | undefined {
    return (
      habilitationDocument.demandeValidationPj?.documents?.[0]?.documentType ??
      habilitationDocument.document?.documentType
    );
  }

  private static isRefused(habilitationDocument: HabilitationDocument): boolean {
    const demandeValidationPj = habilitationDocument.demandeValidationPj;
    return (
      demandeValidationPj?.statutConsultant === 'refuse' ||
      demandeValidationPj?.statutBackOffice === 'refuse'
    );
  }

  private static isGeneratedParcoursDocument(
    habilitationType: HabilitationType,
    documentType: DocumentType
  ): boolean {
    return (
      (habilitationType === 'cif' && documentType === 'conv_distrib_invest') ||
      (habilitationType === 'alpsi' && documentType === 'mandat_alpsi') ||
      (habilitationType === 'immobilier' &&
        (documentType === 'cerfa_cci' || documentType === 'attestation_collaborateur'))
    );
  }

  private static getDocumentLabel(
    documentType: DocumentType | undefined
  ): string | undefined {
    switch (documentType) {
      case 'conv_distrib_invest':
        return 'Convention CIF';
      case 'mandat_alpsi':
        return 'Mandat ALPSI';
      case 'cerfa_cci':
        return 'Cerfa CCI';
      case 'attestation_collaborateur':
        return 'Attestation collaborateur';
      default:
        return undefined;
    }
  }

  private static getHabilitationsDocument(
    demandeHabilitationDocuments: DemandeHabilitationDocument[],
    isBackOffice: boolean
  ): HabilitationDocument[] {
    if (demandeHabilitationDocuments.length > 0) {
      return demandeHabilitationDocuments
        .map(docHab => {
          if (docHab?.document) {
            const document = DocumentMapper.fromGraphQl(
              docHab as DemandeHabilitationDocument,
              isBackOffice
            );
            return {
              document,
              label: this.getDocumentLabel(document?.documentType),
            } as HabilitationDocument;
          } else if (docHab?.demandeValidationPjDocument) {
            const demandeValidationPj = DemandeValidationPjMapper.fromGraphQL(
              docHab.demandeValidationPjDocument!
                .demandeValidationPj as DemandeValidationPjGql,
              isBackOffice
            );
            return {
              demandeValidationPj,
              label: this.getDocumentLabel(
                demandeValidationPj.documents?.[0]?.documentType
              ),
            } as HabilitationDocument;
          } else {
            const documentType = DocumentTypeMapper.fromGraphQl(
              docHab.documentTypeAsEnum
            );

            return documentType
              ? this.getMissingHabilitationDocument(documentType)
              : null;
          }
        })
        .filter((document): document is HabilitationDocument => document !== null);
    }

    return [];
  }

  private static getMissingHabilitationDocument(
    documentType: DocumentType
  ): HabilitationDocument {
    return {
      label: this.getDocumentLabel(documentType),
      demandeValidationPj: {
        type: 'autre',
        statutBackOffice: 'a_affecter',
        statutConsultant: 'a_completer',
        documents: [
          {
            id: 0,
            declarationId: 0,
            documentType,
            dateUpdate: new Date(0),
            files: [],
            statut: 'a_corriger',
          },
        ],
      },
    };
  }
}
